import { Injectable } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import {
  decodeMultiQuery,
  encodeMultiQuery,
  fromHexStringToUint8Array,
  ModbusLink,
  SolarmanWrapper,
  StickLoggerType,
  StickModelType,
  CommunicationChannel,
  TypeLanDevice,
  ABCSerialNumberAndVersion,
  ABCSerialNumberAndVersionComposer,
  DSerialNumberAndVersion,
  DSerialNumberAndVersionComposer,
  LAN_RETRY_POLICY,
} from 'ngx-modbus';
import { BehaviorSubject, Subject } from 'rxjs';
import { print, ProductModelService, Semaphore, wait } from 'ngx-core-services';
import { first } from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';
import { Router } from '@angular/router';
import { Generics } from 'zcs-generic-utils';
import { ToastService, ErrorSinkService } from 'ngx-zcs-components';

import { getStickLoggerInfo, StickLoggerInfo } from './upgrade-by-stick-logger.utils';
import { resolveInverterVersion } from './utils';
import { BlockTranslationsService } from '../block-translations.service';
import { AccessSupportService } from './access-support.service';

const LAN_DEBUG = { ENABLED: true };
const lanLog = (msg: string, data?: unknown) => LAN_DEBUG.ENABLED && console.log(`[Modbus:LAN] ${msg}`, data ?? '');

export interface LanScanResult {
  inverterSerial?: string;
  ipaddress: string;
  mac: string;
  serial: string;
  version?: string;
  model?: StickModelType;
  lanUpgrade?: boolean;
};
export type LanScanResults = LanScanResult[];

const LAN_TIMEOUT = 15 * 1000;
const LAN_RETRY = 5;
const LAN_SCAN_TIMEOUT = 2 * 1000;
const LAN_SCAN_COUNT = 3;

@Injectable({
  providedIn: 'root',
})
export class LanService implements CommunicationChannel {
  readonly connectionStatus$ = new BehaviorSubject<boolean>(false);
  readonly response$ = new Subject<Uint8Array>();
  private responseSubscription: PluginListenerHandle | null = null;
  private readonly throttlerGetSnByModbus = new Semaphore();

  command?: ModbusLink | undefined;
  _isConnected = false;
  get isConnected() {
    return this._isConnected;
  }
  set isConnected(value) {
    this._isConnected = value;
    this.connectionStatus$.next(value);
  }
  refreshByDefault = 40 * 1000;
  deviceInfo: TypeLanDevice | null = null;
  lanScanResults: LanScanResults = [];
  lanIsScanning = false;
  solarmanWrapper: SolarmanWrapper | null = null;
  mode: 'normal' | 'upgrade' = 'normal';

  concurrentQuery = {
    count: 1,
    encodeMultiQuery,
    decodeMultiQuery,
  };

  get deviceId() {
    return `${this.command?.composedData?.SN || this.command?.data?.SN || this.deviceInfo?.serial}`;
  }

  get inverterId() {
    return `${this.command?.composedData?.SN || this.command?.data?.SN || this.deviceInfo?.inverterSerial}`;
  }

  get loggerId(): number | undefined {
    return this.deviceInfo?.serial;
  }

  readonly close = async () => {
    await this.resetContext();

    await Promise.race([
      Generics.closeSocket(),
      wait(3000),
    ]);
  };

  constructor(
    private readonly blockTranslationsService: BlockTranslationsService,
    private readonly accessSupportService: AccessSupportService,
    private readonly errorSink: ErrorSinkService,
    private readonly router: Router,
    private readonly toastService: ToastService,
    private readonly viewportScroller: ViewportScroller,
  ) {
    this.interaction = this.interaction.bind(this);
    this.scan = this.scan.bind(this);
    this.getInverterSerialByModbus = this.throttlerGetSnByModbus.wrap(this.getInverterSerialByModbus.bind(this));
  }

  async resetContext() {
    await this.responseSubscription?.remove();

    this.command?.destroy();
    this.command = undefined;
    this.deviceInfo = null;
    this.isConnected = false;
    this.responseSubscription = null;
    this.solarmanWrapper = null;
  }


  async interaction(cmd: Uint8Array): Promise<Uint8Array> {
    const toSend = Array.from(cmd);
    const toSendAsString = toSend.map((x) => x.toString(16).padStart(2, '0')).join('');
    print(`SEND - 0x${toSendAsString}`);

    const encodeCmd = this.solarmanWrapper?.v5FrameEncoder(cmd);
    if (!encodeCmd) {
      throw new Error('Unable to encode command');
    }
    const message = Array.from(encodeCmd).map((value) => value.toString(16).padStart(2, '0')).join('');
    let attempts = 0;

    while (attempts < LAN_RETRY) {
      try {
        await Generics.sendMessage({ message });
      } catch {
        await this.close();
        await this.router.navigate(['/']);
        throw new Error('Unable to send message');
      }

      let response: Uint8Array;
      try {
        response = await this.response$.pipe(first()).toPromise();
      } catch (err) {
        response = new Uint8Array();
      }

      if (response.length > 0) {
        return response;
      } else {
        attempts++;
      }
    }

    throw new Error('Unable to receive a valid response after multiple attempts');
  }

  async scan() {
    this.lanScanResults = [];
    this.lanIsScanning = true;

    const scanResultHandler = async (result: LanScanResult) => {
      const stickInfo: StickLoggerInfo | null = await getStickLoggerInfo(result.ipaddress);
      if (!this.lanScanResults.some((r) => r.ipaddress === result.ipaddress) && stickInfo) {
        await this.processScanResult(result, stickInfo);
      }
    };

    const scanListener: PluginListenerHandle = Generics.addListener('scanResult', scanResultHandler);

    for (let i = 0; i < LAN_SCAN_COUNT; i++) {
      await Generics.scan();
    }

    await this.waitForScanToComplete(LAN_SCAN_TIMEOUT * LAN_SCAN_COUNT);
    await scanListener.remove();
  }

  async processScanResult(result: LanScanResult, stickInfo: StickLoggerInfo) {
    if (stickInfo.firmwareVersion === undefined) {
      return;
    }

    const inverterSerial = stickInfo?.inverterSerialNumber;
    const stickDeviceInfo = await this.accessSupportService.getDeviceModuleInfo(stickInfo.firmwareVersion);
    if (
      !stickDeviceInfo ||
      stickDeviceInfo.DEVICE_MODEL_ID === 'INVERTER'
    ) {
      return;
    }

    const model = StickLoggerType(stickDeviceInfo.DEVICE_MODEL_ID);
    const version = stickInfo?.firmwareVersion;

    this.lanScanResults.push({
      ...result,
      inverterSerial,
      model,
      version,
      lanUpgrade: stickDeviceInfo.LAN_UPGRADE,
    });
  }

  async getInverterSerialByModbus(selection: LanScanResult): Promise<LanScanResult> {
    if (!(selection.version && await this.accessSupportService.isSupported(selection.version))) {
      this.toastService.simple(this.blockTranslationsService.labels.commonUnsupportedStickLogger);
      return selection;
    }

    this.toastService.simple(this.blockTranslationsService.labels.commonWaits, 1500);

    await this.open({
      ...selection,
      address: selection.ipaddress,
      port: 8899,
      serial: Number(selection.serial),
    });
    const cmd = new ModbusLink({
      executeCmd: this.interaction,
      concurrentQuery: this.concurrentQuery,
      modbusAddress: 1,
      maxTimeoutInMs: LAN_TIMEOUT,
      retryPolicy: LAN_RETRY_POLICY,
      handleFatalError: async (error) => {
        this.errorSink.reportTransport(
          'Connection lost during scan',
          `LAN scan error: ${error?.message ?? error}`,
        );
      },
    });

    await cmd.executeMixedRequests([{
      mode: 'read',
      table: DSerialNumberAndVersion,
      composer: DSerialNumberAndVersionComposer,
    }]);


    const product = new ProductModelService(cmd.composedData.SN as string || '');
    /**
     * If I don't recognize it as an inverter it may be because I'm using the wrong protocol so I try again
     * with the ABC protocol
     */
    if (product.type?.name !== 'inverter') {
      await cmd.executeMixedRequests([{
        mode: 'read',
        table: ABCSerialNumberAndVersion,
        composer: ABCSerialNumberAndVersionComposer,
      }]);
    }

    const inverterSerial = cmd.composedData.SN as string;
    await this.close();

    this.lanScanResults.forEach((r) => {
      if (r.ipaddress === selection.ipaddress) {
        r.inverterSerial = inverterSerial;
      }
    });

    return {
      ...selection,
      inverterSerial,
    };
  }

  waitForScanToComplete(timeout: number): Promise<LanScanResults> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.lanIsScanning = false;
        resolve(this.lanScanResults);
      }, timeout);
    });
  }

  async open(device: TypeLanDevice): Promise<void> {
    let openSocketCountRetry = 3;
    while (openSocketCountRetry > 0) {
      try {
        await Promise.race([
          Generics.openSocket({
            host: device.address,
            port: device.port,
          }),
          wait(3000).then(() => {
            throw new Error('Unable to open socket');
          }),
        ]);
        break;
      } catch (error) {
        await Promise.race([
          wait(3000),
          Generics.closeSocket(),
        ]);
        openSocketCountRetry--;
        if (openSocketCountRetry === 0) {
          await this.toastService.simple(this.blockTranslationsService.labels.commonUnableToOpenSocket);
          throw error;
        }
      }
    }

    await this.resetContext();

    this.responseSubscription = Generics.addListener('dataReceived', (received) => {
      const response = fromHexStringToUint8Array(received.data);
      print(`RECEIVE - 0x${Array.from(response).map((x) => x.toString(16).padStart(2, '0')).join(' ')}`);

      const decodeResponse = this.solarmanWrapper?.v5FrameDecoder(response);
      if (!decodeResponse) {
        throw new Error('Unable to decode response');
      }
      print(`RECEIVE DECODED - 0x${Array.from(decodeResponse).map((x) => x.toString(16).padStart(2, '0')).join(' ')}`);
      this.response$.next(decodeResponse);
    });

    this.isConnected = true;
    this.deviceInfo = device;
    const handleFatalError = async (error?: any) => {
      this.errorSink.reportTransport(
        'Connection lost',
        `Fatal LAN error for device ${lanSerial}`,
      );
      await this.close();
      await this.resetContext();
      await this.router.navigate(['/']).then(() => {
        this.viewportScroller.scrollToPosition([0, 0]);
      });
    };

    const lanSerial = device.inverterSerial || `${device.serial}` || device.address;
    const inverterVersion = device.inverterSerial ? resolveInverterVersion(device.inverterSerial) : undefined;
    lanLog(` ModbusLink: serial="${lanSerial}", inverterVersion=${inverterVersion ?? 'legacy'}`);

    this.command = new ModbusLink({
      deviceId: lanSerial,
      inverterVersion,
      executeCmd: this.interaction,
      handleFatalError,
      modbusAddress: 1,
      maxTimeoutInMs: LAN_TIMEOUT,
      concurrentQuery: this.concurrentQuery,
      retryPolicy: LAN_RETRY_POLICY,
    });
    this.command.reset();
    this.solarmanWrapper = new SolarmanWrapper(device.serial, this.deviceInfo?.model);
  }
}
